import { useEffect, useState } from 'react';

import { eventsFilteredArgs, fetchEventCards } from '@/lib/prefetch';
import { LIST_BUCKET } from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';
import type { DbEvent } from '@todo/core/supabase';

type Loaded = { when: string; rows: DbEvent[] };

/**
 * An event card list: the build-time seed (`seedKey`, built for `seedWhen` —
 * src/lib/prefetch.ts) on the first render, so hydration matches the static
 * HTML; event_shows_filtered in the browser for any other `when` (a getDateRange
 * id, or 'any' for the hub's whole window), and when the seed isn't on the page
 * (a client-side navigation). The rows on screen stay until the new ones land.
 */
export function useEventCards(seedKey: string, seedWhen: string, when: string, limit?: number) {
  const [loaded, setLoaded] = useState<Loaded | null>(() => {
    const seed = getPrefetched<DbEvent[]>(LIST_BUCKET, seedKey);
    return seed ? { when: seedWhen, rows: seed } : null;
  });
  const [error, setError] = useState(false);

  useEffect(() => {
    if (loaded?.when === when) return;
    let cancelled = false;
    setError(false);
    fetchEventCards(eventsFilteredArgs(when), limit)
      .then((rows) => {
        if (!cancelled) setLoaded({ when, rows });
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
    // `loaded` is deliberately not a dependency: it changes only through here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [when, limit]);

  return { events: loaded?.rows ?? [], loading: loaded?.when !== when && !error, error };
}
